import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Target, Loader2 } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1574629810360-7efbbe195018?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxzb2NjZXIlMjBiYWxsJTIwZmllbGQlMjBncmFzc3xlbnwwfHx8fDE3ODYzMTg5Njl8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A0A0A]">
      <div className="relative hidden lg:block overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
        <div className="absolute bottom-0 left-0 p-12">
          <div className="flex items-center gap-3 mb-6">
            <img src="/crest.png" alt="5N2 Footy Academy crest" className="w-14 h-14 object-contain" />
            <span className="font-display text-2xl font-black uppercase tracking-tight">
              5N2 Academy
            </span>
          </div>
          <h1 className="font-display text-6xl font-black uppercase leading-[0.9] tracking-tighter">
            5N2 Academy<br /><span className="text-[#2E8B57]">Player</span> Portal
          </h1>
          <p className="text-[#A1A1AA] mt-6 max-w-md">
            The coach's command center for your squad. Profiles, performance data, and progress — all in one place.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-sm"
        >
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <img src="/crest.png" alt="5N2 Footy Academy crest" className="w-9 h-9 object-contain" />
            <span className="font-display text-xl font-black uppercase">5N2 Academy</span>
          </div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#71717A] mb-2">
            Coach Access
          </p>
          <h2 className="font-display text-4xl font-black uppercase tracking-tight mb-8">
            Sign in
          </h2>
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.2em] text-[#A1A1AA]">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-[#0A0A0A] border-[#27272A] rounded-none h-12 focus:border-[#15803D] focus:ring-1 focus:ring-[#15803D]"
                placeholder="coach@team.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.2em] text-[#A1A1AA]">
                Password
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-[#0A0A0A] border-[#27272A] rounded-none h-12 focus:border-[#15803D] focus:ring-1 focus:ring-[#15803D]"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm text-red-500 font-medium">
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-12 rounded-none bg-[#15803D] hover:bg-[#166534] text-white font-bold uppercase tracking-[0.15em] transition-colors"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Enter Command Center"}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
